"use client";

import React, { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { useQuery } from "@apollo/client";
import defaultProfile from "@/images/default-profile.jpg";
import { SUGGESTED_USERS } from "@/api/apollo-client/queries/Queries";
import SkeletonLoader from "./SkeletonLoader";
import FollowButton, { ButtonVariant } from "./followButton";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/authContext";
import { HoverableMention } from "./HoverableMention";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SuggestedUserCard from "@/components/SuggestedUserCard";
import SingleSuggestedCard from "./SingleSuggestedCard";

export interface MentionedUser {
  id: string;
  name: string;
  display_name?: string;
  profile_picture?: {
    url: string;
    thumbnail_url: string;
  };
}

const WhoToFollow = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<MentionedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Local display-loading state
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const router = useRouter();

  const { data, error } = useQuery(SUGGESTED_USERS, {
    variables: {
      input: {
        user_id: user?.id,
        pagination: {
          first: 5,
          after_name: null,
        },
      },
    },
    skip: !user,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.suggestedUsers) {
      setIsLoading(true); // keep skeleton visible

      const timer = setTimeout(() => {
        setUsers(data.suggestedUsers);
        setActiveIndex(0);
        setIsLoading(false); // now show real data
      }, 1); // simulate 2-second delay

      return () => clearTimeout(timer);
    }
  }, [data]);

  const handleNext = () => {
    if (!users.length) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = 246; // card width + gap
    const maxIndex = Math.max(users.length - 1, 0);
    const newIndex = Math.min(activeIndex + 1, maxIndex);
    setActiveIndex(newIndex);
    container.scrollTo({
      left: newIndex * cardWidth,
      behavior: "smooth",
    });
  };

  const handlePrev = () => {
    if (!users.length) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = 246;
    const newIndex = Math.max(activeIndex - 1, 0);
    setActiveIndex(newIndex);
    container.scrollTo({
      left: newIndex * cardWidth,
      behavior: "smooth",
    });
  };

  if (!isLoading && users.length === 0 && !error) {
    return null;
  }

  return (
    <div className="bg-white/25 dark:bg-white/10 rounded-2xl border border-white/75 dark:border-white/25 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px]">
      <div className="mx-4 mb-1.5 p-1 rounded-2xl">
        <h2 className="font-bold p-1 text-xl mb-1.5">Suggested Users</h2>

        <div className="space-y-4">
          {isLoading && (
            <SkeletonLoader variant="list" count={5} showShimmer={true} />
          )}
          {error && (
            <p className="text-sm text-red-500">Failed to load suggestions.</p>
          )}

          {!isLoading && users.length > 0 && (
            <div className="relative">
              <div className="overflow-hidden">
                <div
                  ref={scrollContainerRef}
                  className="flex gap-3 overflow-x-auto pb-1 pr-2 pl-1 scroll-smooth [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  {users.map((user) => (
                    <div
                      className={`flex-shrink-0 border rounded-3xl bg-white/90 dark:bg-gray-900/90 ${
                        users.length === 1 ? "w-full" : "w-[230px]"
                      }`}
                      key={user.id}
                      onClick={() => {
                        router.push(`/profile/${user?.name}`);
                      }}
                    >
                      {users.length === 1 ? (
                        <SingleSuggestedCard user={user} />
                      ) : (
                        <SuggestedUserCard user={user} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {activeIndex > 0 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="absolute -left-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white/90 dark:bg-gray-900/90 hover:bg-white dark:hover:bg-gray-800 shadow-md transition-all"
                  aria-label="Scroll suggested users left"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}

              {activeIndex < users.length - 1 && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="absolute -right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white/90 dark:bg-gray-900/90 hover:bg-white dark:hover:bg-gray-800 shadow-md transition-all"
                  aria-label="Scroll suggested users right"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhoToFollow;
